import { ParameterType } from '@/study-search/enums';
import { PageTitleHandler } from '../PageTitleHandler';
import { Filter } from '../../filter/Filter';
import { PortalType } from '@studyportals/domain-client';
import { IMetaInfo } from '@/study-search/interfaces';
import getters from '@/study-search/store/getters';
import { PortalsName } from '../PortalsName';

export abstract class TitleBase extends PageTitleHandler {
	protected readonly titlePortalType: string = getters.getPortalDefinition().titlePortalType;
	protected readonly portalType: PortalType = getters.getPortalType();
	protected readonly portalName: string = this.getPortalName();

	protected get filters(): Filter[] {
		return getters.getFilters();
	}

	protected get indexableFilters(): Filter[] {
		return this.filters.filter((_) => _.canBeIndexed());
	}

	protected get multiValueFilters(): Filter[] {
		return this.filters.filter((_) => _.values.length > 1);
	}

	protected get totalSearchResults(): number {
		return getters.getTotalSearchResults();
	}

	protected handle(): IMetaInfo {
		return {
			pageTitle: this.getPageTitle(),
			metaTitle: this.getMetaTitle(),
			metaDescription: this.getMetaDescription(),
		};
	}

	protected getFilterValue(type: ParameterType): string {
		const filter = this.filters.find((_) => _.type === type);
		if (!filter) {
			return '';
		}

		if (filter.definition) {
			return filter.definition.getFilterOption(filter.values[0])?.optionTitle;
		}

		return filter.values[0];
	}

	protected getFilterMetaTitleValue(type: ParameterType): string {
		const filter = this.filters.find((_) => _.type === type);
		if (!filter) {
			return '';
		}

		return filter.definition?.getFilterOption(filter.values[0])?.optionMetaTitle || '';
	}

	protected abstract getPageTitle(): string;

	protected getMetaTitle(): string {
		return `${this.totalSearchResults} ${this.getPageTitle()} | ${this.portalName}`;
	}

	protected getMetaDescription(): string {
		switch (this.portalType) {
			case PortalType.BACHELOR:
				return (
					'Find the best Bachelors from top universities. ' + 'Search all BA, BSc, LLB and more undergraduate programmes abroad or online'
				);
			case PortalType.DISTANCE_LEARNING:
				return '';
			case PortalType.MASTER:
				return 'Find the best Masters from top universities. ' + 'Search all MBA, MSc, MA, LLM, MPhil and more programmes abroad or online';
			case PortalType.PHD:
				return (
					'Find the best PhD programmes from top universities. ' + 'Search all PhD and postgraduate doctorate programmes abroad or online'
				);
			case PortalType.SHORT:
				return 'Find the best short courses, professional programmes, ' + 'summer schools at Universities and Business Schools worldwide';
		}
	}

	protected buildMetaDescription(programmeType: string, location = ''): string {
		return `Find the best ${programmeType} from top universities${location}. Check all ${this.totalSearchResults} programmes.`;
	}

	private getPortalName(): string {
		switch (this.portalType) {
			case PortalType.PHD:
				return PortalsName.PHD;
			case PortalType.MASTER:
				return PortalsName.MASTER;
			case PortalType.BACHELOR:
				return PortalsName.BACHELOR;
			case PortalType.DISTANCE_LEARNING:
				return PortalsName.DISTANCE_LEARNING;
			case PortalType.SHORT:
				return PortalsName.SHORT_COURSE;
		}
	}
}
